<script lang="ts">
	import { format } from 'svelte-i18n';
	import { onMount } from 'svelte';
	import type { Device } from 'src/types/Device';
	import type { CloudRecordingDayOverview } from 'src/components/Camera/types/cloud-recordings';
	import {
		AdvancedCardV2,
		ButtonV2,
		Datepicker,
		Icon,
		LabelListV2,
		Loader,
		Modal,
		Text,
		toast,
	} from '@Shelly/shelly-design-system/index';
	import {
		deleteDeviceRecordings,
		getDeviceRecordingsOverview,
	} from 'src/services/cam-cloud/recordings';

	export let device: Device;

	// Placeholder until the BE exposes the encryption state in the cloud settings.
	const isEncrypted = false;

	let showDeleteAllModal = false;
	let showDeleteByDateModal = false;
	let deleteAllLoading = false;
	let deleteByDateLoading = false;
	let selectedDate: string | null = null;

	let overviewDays: CloudRecordingDayOverview[] = [];
	let overviewLoading = true;

	$: deviceId = device?.id;
	$: totalDays = overviewDays.length;
	$: totalVideos = overviewDays.reduce((sum, day) => sum + (day.videos ?? 0), 0);

	$: overviewLabels = overviewLoading
		? []
		: [
				{
					id: 'videos',
					icon: 'fal fa-video',
					value: $format('camera_cloud_overview_videos', {
						values: { count: totalVideos },
						default: `${totalVideos} videos`,
					}),
				},
				{
					id: 'days',
					icon: 'fal fa-calendar-day',
					value: $format('camera_cloud_overview_days', {
						values: { count: totalDays },
						default: `${totalDays} days`,
					}),
				},
			];

	const loadOverview = async () => {
		if (!deviceId) return;
		overviewLoading = true;
		try {
			const { days } = await getDeviceRecordingsOverview(deviceId);
			overviewDays = days ?? [];
		} catch (error) {
			console.log('Failed to load recordings overview', error);
		} finally {
			overviewLoading = false;
		}
	};

	const showResultToast = (deleted: number) => {
		toast.open({
			type: 'success',
			title: $format('camera_cloud_recordings_deleted_toast', {
				values: { count: deleted },
				default: `Deleted ${deleted} recordings.`,
			}),
		});
	};

	const showErrorToast = () => {
		toast.open({
			type: 'danger',
			title: $format('camera_cloud_recordings_delete_error', {
				default: 'Could not delete recordings. Please retry.',
			}),
		});
	};

	const onDeleteAll = async () => {
		deleteAllLoading = true;
		try {
			const { deleted } = await deleteDeviceRecordings(deviceId);
			showResultToast(deleted);
			showDeleteAllModal = false;
			await loadOverview();
		} catch (error) {
			console.log('Failed to delete recordings', error);
			showErrorToast();
		}
		deleteAllLoading = false;
	};

	const handleDateChange = (event: CustomEvent) => {
		selectedDate = event.detail?.[1] ?? null;
	};

	const onDeleteByDate = async () => {
		if (!selectedDate) return;
		deleteByDateLoading = true;
		try {
			const { deleted } = await deleteDeviceRecordings(deviceId, selectedDate);
			showResultToast(deleted);
			showDeleteByDateModal = false;
			selectedDate = null;
			await loadOverview();
		} catch (error) {
			console.log('Failed to delete recordings', error);
			showErrorToast();
		}
		deleteByDateLoading = false;
	};

	onMount(() => {
		loadOverview();
	});
</script>

<AdvancedCardV2
	class="mb-s"
	aria-label={$format('camera_cloud_overview_title', { default: 'Camera Cloud Storage' })}
	title={$format('camera_cloud_overview_title', { default: 'Camera Cloud Storage' })}
	avatar={{ icon: 'fal fa-camera-web', backgroundColor: 'gradient-pink-purple' }}
>
	<LabelListV2 slot="main" size="s" labelList={overviewLabels} a11yProps={{ isInteractive: false }} />
	<div slot="right" class="d-flex align-items-center">
		{#if overviewLoading}
			<Loader size="m" />
		{:else if isEncrypted}
			<Icon
				size="m"
				icon="fal fa-shield-check"
				class="text-success"
				aria-label={$format('camera_cloud_overview_encrypted', {
					default: 'Recordings are encrypted',
				})}
			/>
		{:else}
			<Icon
				size="m"
				icon="fal fa-cloud-check"
				class="text-success"
				aria-label={$format('camera_cloud_overview_secured', {
					default: 'Recordings are securely stored',
				})}
			/>
		{/if}
	</div>
</AdvancedCardV2>

<AdvancedCardV2
	class="mb-s"
	aria-label={$format('camera_cloud_delete_by_date_title', { default: 'Delete recordings by date' })}
	title={$format('camera_cloud_delete_by_date_title', { default: 'Delete recordings by date' })}
	avatar={{ icon: 'fal fa-calendar-day' }}
	subtitle={{
		type: 'muted',
		bold: false,
		textSmall: true,
		value: $format('camera_cloud_delete_by_date_desc', {
			default: "Remove a day's recordings",
		}),
	}}
>
	<div slot="right" class="d-flex align-items-center">
		<ButtonV2
			size="s"
			variant="secondary"
			on:click={() => (showDeleteByDateModal = true)}
			aria-label={$format('button_delete', { default: 'Delete' })}
		>
			{$format('button_delete', { default: 'Delete' })}
		</ButtonV2>
	</div>
</AdvancedCardV2>

<AdvancedCardV2
	aria-label={$format('camera_cloud_delete_all_title', { default: 'Delete all recordings' })}
	title={$format('camera_cloud_delete_all_title', { default: 'Delete all recordings' })}
	avatar={{ icon: 'fal fa-trash' }}
	subtitle={{
		type: 'muted',
		bold: false,
		textSmall: true,
		value: $format('camera_cloud_delete_all_desc', {
			default: 'Delete all cloud recordings',
		}),
	}}
>
	<div slot="right" class="d-flex align-items-center">
		<ButtonV2
			size="s"
			variant="danger"
			on:click={() => (showDeleteAllModal = true)}
			aria-label={$format('button_delete', { default: 'Delete' })}
		>
			{$format('button_delete', { default: 'Delete' })}
		</ButtonV2>
	</div>
</AdvancedCardV2>

<Modal
	showModal={showDeleteAllModal}
	title={$format('camera_cloud_delete_all_confirm_title', { default: 'Delete all recordings?' })}
	backdropClose={false}
	on:close={() => (showDeleteAllModal = false)}
>
	<svelte:fragment slot="content">
		<Text>
			{$format('camera_cloud_delete_all_confirm_message', {
				default:
					'This will permanently delete every cloud recording for this camera. This action cannot be undone.',
			})}
		</Text>
	</svelte:fragment>
	<div slot="footer" class="d-flex justify-content-end">
		<ButtonV2
			class="mr-s"
			variant="secondary"
			disabled={deleteAllLoading}
			on:click={() => (showDeleteAllModal = false)}
			aria-label={$format('button_cancel', { default: 'Cancel' })}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</ButtonV2>
		<ButtonV2
			variant="danger"
			loading={deleteAllLoading}
			on:click={onDeleteAll}
			aria-label={$format('button_delete', { default: 'Delete' })}
		>
			{$format('button_delete', { default: 'Delete' })}
		</ButtonV2>
	</div>
</Modal>

<Modal
	showModal={showDeleteByDateModal}
	title={$format('camera_cloud_delete_by_date_modal_title', { default: 'Delete recordings by date' })}
	backdropClose={false}
	on:close={() => (showDeleteByDateModal = false)}
>
	<svelte:fragment slot="content">
		<Datepicker
			id="camera-cloud-delete-date"
			aria-label={$format('camera_cloud_delete_by_date_picker', { default: 'Select date' })}
			a11yProps={{
				inputAriaLabel: 'date input',
				selectMonthAriaLabel: 'select month',
				selectYearAriaLabel: 'select year',
			}}
			maxDate={new Date()}
			label={$format('camera_cloud_delete_by_date_picker', { default: 'Select date' })}
			on:change={handleDateChange}
		/>
	</svelte:fragment>
	<div slot="footer" class="d-flex justify-content-end">
		<ButtonV2
			class="mr-s"
			variant="secondary"
			disabled={deleteByDateLoading}
			on:click={() => (showDeleteByDateModal = false)}
			aria-label={$format('button_cancel', { default: 'Cancel' })}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</ButtonV2>
		<ButtonV2
			variant="danger"
			loading={deleteByDateLoading}
			disabled={!selectedDate || deleteByDateLoading}
			on:click={onDeleteByDate}
			aria-label={$format('button_delete', { default: 'Delete' })}
		>
			{$format('button_delete', { default: 'Delete' })}
		</ButtonV2>
	</div>
</Modal>
